import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { BiPlus, BiPlusCircle, BiUpArrowCircle, BiX } from 'react-icons/bi';
import { Box, Flex } from 'rebass';
import Modal from 'app/modal/v3';
import GiftCertificateSuccessMessage from 'gift-certificate/GiftCertificateSuccessMessage';
import {
  JournalCard,
  CardTitle,
  SpecRow,
  SpecText,
  DetailsLink,
  EntitlementChip,
  RemoveButton,
  ButtonRow,
  PrimaryButton,
  OutlineButton,
  AddOnsLink,
} from '../styled';
import { entitlementLabel, specLine } from '../entitlements';
import { resendGiftCertificateEmail } from '../api';
// Recipient label comes from the gift certificate's own recipient field (set in
// "Personalize your gift certificate"), with the baby's name appended when known.
// Repeat gifts to the same recipient/child are suffixed - 2, - 3, … by the caller.
export const giftTitle = (gift, suffix) => {
  const parts = [gift.recipientName, gift.babyName].filter(Boolean).join(' - ');
  const base = parts || 'Recipient TBD';
  return suffix > 1 ? `Gift to: ${base} - ${suffix}` : `Gift to: ${base}`;
};

const GiftCard = ({
  gift,
  titleSuffix,
  hasSelection,
  pendingChips,
  onDetails,
  onEditCertificate,
  onAddToBook,
  onRemovePending,
  onPurchase,
  onUpgradeBundle,
  isAddOnsMode
}) => {

  const [isLoading, setIsLoading] = useState(false);
  const [showResendSuccess, setShowResendSuccess] = useState(false);

  // Once redeemed the journal belongs to the recipient, so the giver can no longer
  // edit the certificate or move its entitlements.
  const isRedeemed = String(gift.state || '').toLowerCase() === 'redeemed';
  const spec = specLine(gift.entitlements);
  // Allocation needs a journal to target; a certificate without one can't receive
  // an add-on, so it keeps its normal actions.
  const canReceiveAddOn = hasSelection && !isRedeemed && !!gift.childJournalId;
  // Dynamic per the design — the recipient's actual name, never a placeholder.
  const allocateLabel = 'Add to this book';


  if (showResendSuccess)
    return <Box sx={{
      height: '100vh',
      width: '100vw',
      position: 'fixed',
      top: 0,
      left: 0,
      bg: 'white'
    }}> <GiftCertificateSuccessMessage
        onContinue={() => setShowResendSuccess(false)}
        continueLabel="Close"
        minHeight="auto"
        mt={7}
      />
    </Box>

  return (
    <JournalCard >
      <CardTitle>{giftTitle(gift, titleSuffix)}</CardTitle>

      <SpecRow>
        <SpecText>{spec || 'Baby book'}</SpecText>
        <DetailsLink type="button" onClick={() => onDetails(gift)}>
          Details
        </DetailsLink>
      </SpecRow>

      {pendingChips.map(chip => (
        <EntitlementChip key={chip.id} pending>
          {entitlementLabel(chip)}
          <RemoveButton type="button" onClick={() => onRemovePending(chip.id)}>
            Remove
            <BiX size={20} />
          </RemoveButton>
        </EntitlementChip>
      ))}

      {/* While an add-on is selected the gift-management actions give way to the
          allocation action, matching the Unassigned tab's treatment. */}
      {(canReceiveAddOn || pendingChips.length > 0 || isAddOnsMode) ? (
        <OutlineButton
          type="button"
          disabled={!hasSelection}
          onClick={() => onAddToBook(gift.childJournalId)}
        >
          <BiPlus size={20} />
          {allocateLabel}
        </OutlineButton>
      ) : (
        !isRedeemed && (
          <ButtonRow>
            <OutlineButton type="button" onClick={() => onEditCertificate(gift)}>
              Edit Certificate
            </OutlineButton>
            <PrimaryButton disabled={isLoading} type="button" onClick={async () => {
              setIsLoading(true);
              try {
                await resendGiftCertificateEmail(gift.id);
                setShowResendSuccess(true);
              } finally {
                setIsLoading(false);
              }
            }}>
              Resend Email
            </PrimaryButton>
          </ButtonRow>
        )
      )}

      {/* Redeemed gifts belong to the recipient now — the API itself refuses a
          purchase on one ("This gift has been redeemed and can no longer be
          changed."), so the entry point is hidden rather than offering an action
          guaranteed to 422. */}
      {!!gift.childJournalId && (<Flex py={1}>
        {gift.entitlements?.['plan-name'] !== 'Platinum Bundle' && <AddOnsLink type="button" onClick={() => onUpgradeBundle(gift)}>
          <BiUpArrowCircle size={18} />
          Upgrade Bundle
        </AddOnsLink>}
        {gift.entitlements?.['plan-name'] !== 'Platinum Bundle' && <Flex width="1px" bg="#D1D1D1" height="44px" />}
        <AddOnsLink type="button" onClick={() => onPurchase(gift)}>
          <BiPlusCircle size={18} />
          Add-ons
        </AddOnsLink>
      </Flex>
      )}
    </JournalCard>
  );
};

GiftCard.propTypes = {
  gift: PropTypes.shape({
    id: PropTypes.string.isRequired,
    recipientName: PropTypes.string,
    babyName: PropTypes.string,
    state: PropTypes.string,
    childJournalId: PropTypes.string,
    entitlements: PropTypes.object,
  }).isRequired,
  titleSuffix: PropTypes.number,
  hasSelection: PropTypes.bool,
  pendingChips: PropTypes.array,
  disabled: PropTypes.bool,
  onDetails: PropTypes.func.isRequired,
  onEditCertificate: PropTypes.func.isRequired,
  onAddToBook: PropTypes.func,
  onRemovePending: PropTypes.func,
  onPurchase: PropTypes.func,
  onUpgradeBundle: PropTypes.func,
};

GiftCard.defaultProps = {
  titleSuffix: 1,
  hasSelection: false,
  pendingChips: [],
  disabled: false,
  onAddToBook: () => { },
  onRemovePending: () => { },
  onPurchase: () => { },
  onUpgradeBundle: () => { },
};

export default GiftCard;
